import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import './App.css';
import useStore from './store/useStore';
import { setStoreGetter, api, clearAuthData } from './services/authService';
import authService from './services/authService';
import keycloak, { keycloakAdmin } from './keycloak';
import clientConfig from './helpers/clientConfig';
import logger from './utils/logger';
import queryClient from './queryClient';
import ErrorBoundary from './components/ErrorBoundary';
import { ALL_OPS_ROLES, OPS_ADMIN_PAGE_ROLES } from './helpers/roleMap';

// Routed pages
const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const AgentPage = lazy(() => import('./pages/AgentPage'));
const AgentPersonalStats = lazy(() => import('./pages/agent/AgentPersonalStats'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const InvitePage = lazy(() => import('./pages/InvitePage'));
const PortalLayout = lazy(() => import('./pages/portal/PortalLayout'));
const AgentLayout = lazy(() => import('./pages/agent/AgentLayout'));

// Auth wrapper component - sayfa yenilendiğinde oturum durumunu kontrol eder
const AuthWrapper = ({ children }) => {
  const { token, user, isAuthenticated, setUser, setToken } = useStore();
  const [authChecked, setAuthChecked] = useState(false);

  // useRef holds the latest refreshAndContinue so intervals/effects capture the current version
  // without needing to restart when setToken/setUser change (Zustand setters are stable anyway,
  // but this pattern is future-proof against any new deps being added).
  // PLT-35: token refresh delegates to authService.refreshToken(), which now goes through
  // Keycloak's own updateToken() (see keycloak.js ensureKcToken) instead of the removed
  // /auth/refresh endpoint.
  const refreshAndContinueRef = React.useRef(null);
  refreshAndContinueRef.current = async () => {
    try {
      const refreshResponse = await authService.refreshToken();
      if (refreshResponse && refreshResponse.token) {
        setToken(refreshResponse.token);
        if (refreshResponse.user) setUser(refreshResponse.user);
        return true;
      }
    } catch (error) {
      // FE-5: Raw error objesi loglanmıyor — token fragment sızıntısı riski.
      logger.error('Otomatik token yenileme hatası:', error?.message || 'Token yenilenemedi', { status: error?.response?.status });
    }
    return false;
  };
  const refreshAndContinue = (...args) => refreshAndContinueRef.current(...args);

  // Sayfa yüklendiğinde token'ı otomatik kontrol et ve gerekirse yenile
  useEffect(() => {
    const checkToken = async () => {
      try {
        const isValid = await authService.checkTokenValidity();
        if (!isValid) {
          await refreshAndContinueRef.current();
        }
      } catch (error) {
        logger.error('Token kontrol hatası:', error);
      }
    };

    checkToken();

    const tokenCheckInterval = setInterval(checkToken, 5 * 60 * 1000);

    return () => clearInterval(tokenCheckInterval);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // WS-008: eski build'lerin ?apiKey= ile sakladığı legacy ak_ anahtarını temizle.
        localStorage.removeItem('apiKey');
        // Access token sadece memory'de yaşıyor — sayfa yenilemesinde kaybolur.
        // PLT-35: identity-service artık /auth/refresh sunmuyor (PLT-18) — oturumu
        // Keycloak'un kendi check-sso oturumu + updateToken() mekanizmasıyla yeniden kur.
        if (!keycloak.didInitialize) {
          try {
            await keycloak.init({ onLoad: 'check-sso', pkceMethod: 'S256', checkLoginIframe: false });
          } catch (kcErr) {
            logger.error('Keycloak init hatası (session restore):', kcErr?.message || kcErr);
          } finally {
            keycloak.didInitialize = true;
          }
        }
        const restored = await refreshAndContinueRef.current();
        if (!restored) {
          clearAuthData();
        }
      } finally {
        setAuthChecked(true);
      }
    };

    checkAuth();
  }, [setUser, setToken]);

  // Interceptorlar için store getter fonksiyonu tanımla
  useEffect(() => {
    const getState = () => ({
      token,
      user,
      refreshToken: async () => {
        return await refreshAndContinue();
      }
    });

    // Interceptor'ları ayarla ve store getter fonksiyonunu sağla
    setStoreGetter(getState);
  }, [token, user, setToken, setUser]);

  // Yükleme durumunda spinner göster
  if (!authChecked) {
    return (
      <div className="min-h-screen h-[100vh] flex items-center justify-center bg-surface-base p-4">
        <div className="w-96 bg-surface-card shadow-xl rounded-2xl p-6">
          <div className="flex flex-col items-center text-center p-6">
            <div className="animate-spin h-10 w-10 border-4 border-accent border-t-transparent rounded-full"></div>
            <h2 className="text-lg font-semibold text-text-primary mt-4">Oturum Kontrol Ediliyor</h2>
            <p className="text-sm text-text-muted">Lütfen bekleyin...</p>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

// Admin/Supervisor sayfası için koruma komponenti (supervisor+ erişebilir)
// /admin-login üzerinden girildiğinde keycloakAdmin instance'ı kullanılır.
const AdminRoute = ({ children }) => {
  const { isAdmin, isSupervisor, isAuthenticated } = useStore();

  useEffect(() => {
    if (!isAuthenticated) {
      keycloakAdmin.login();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;
  if (!(isAdmin || isSupervisor)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Agent sayfası için koruma komponenti
const AgentRoute = ({ children }) => {
  const { isAgent, isSupervisor, isAdmin, isAuthenticated } = useStore();

  useEffect(() => {
    if (!isAuthenticated) {
      keycloak.login();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;
  if (!(isAgent || isSupervisor || isAdmin)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Portal (company_admin) sayfası için koruma komponenti
// FE-186 Faz 4 Adım 4: admin/supervisor de kabul edilir (eski /admin -> /portal/dashboard yönlendirmesi için).
const CompanyAdminRoute = ({ children }) => {
  const { user, isAdmin, isSupervisor, isAuthenticated } = useStore();

  useEffect(() => {
    if (!isAuthenticated) {
      keycloak.login();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;
  if (!(isAdmin || isSupervisor || user?.role === 'company_admin')) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Keycloak path — USE_KEYCLOAK=true ise legacy AuthWrapper'ın yerine geçer
// Supports two KC clients:
//   /login        → keycloak (ops-dashboard)       → role must be 'agent'
//   /admin-login  → keycloakAdmin (ops-dashboard-admin) → role must be 'admin'/'supervisor'
const KeycloakWrapper = ({ children }) => {
  const { setUser, setToken } = useStore();
  const [kcReady, setKcReady] = useState(false);
  const [kcError, setKcError] = useState(null);
  // FE-3: role mismatch dallarında setKcReady hiç çağrılmıyordu — activeKc.logout()'un
  // redirect'i gecikirse/başarısız olursa kullanıcı süresiz spinner'da kalıyordu, hiçbir
  // açıklama veya çıkış yolu yoktu. Artık hemen 403 ekranı + doğru login linki gösteriliyor;
  // KC logout arka planda devam ediyor.
  const [kcMismatch, setKcMismatch] = useState(null);

  useEffect(() => {
    // HTTPS zorunlu — Web Crypto API (PKCE) HTTP'de çalışmaz
    if (typeof window !== 'undefined' && window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
      window.location.replace('https://' + window.location.host + window.location.pathname + window.location.search);
      return;
    }

    // Hangi KC client'ı kullanacağımızı giriş path'inden belirle
    const isAdminLogin = window.location.pathname.startsWith('/admin-login');
    const activeKc = isAdminLogin ? keycloakAdmin : keycloak;

    // Çift init koruması — strict mode veya re-render sonrası tekrar çalışmasın
    if (activeKc.didInitialize) {
      if (activeKc.authenticated) setKcReady(true);
      return;
    }

    // Token'ları sessionStorage'dan değil, KC'nin kendi session cookie'sinden (check-sso
    // iframe round-trip) yeniden kur — sessionStorage'da JS-okunabilir KC token tutmak XSS riski.
    activeKc.init({ onLoad: 'check-sso', pkceMethod: 'S256', checkLoginIframe: false })
      .then(async authenticated => {
        activeKc.didInitialize = true;
        if (!authenticated) {
          setKcReady(true); // homepage (rol seçici) görünsün
          return;
        }

        const decoded = jwtDecode(activeKc.token);
        const kcUser = {
          id: decoded.sub,
          email: decoded.email,
          role: decoded.role,
          tenantId: decoded.tenantId,
          firstName: decoded.given_name,
          lastName: decoded.family_name,
          first_name: decoded.given_name,
          last_name: decoded.family_name,
          display_name: decoded.display_name || `${decoded.given_name || ''} ${decoded.family_name || ''}`.trim(),
          username: decoded.preferred_username
        };

        // Rol kapı kontrolü — yanlış client'tan giren kullanıcıyı çıkar
        if (isAdminLogin && kcUser.role === 'agent') {
          // Agent, admin-login'den giriş yapmaya çalıştı — reddet
          const targetUrl = window.location.origin + '/login';
          setKcMismatch({
            message: 'Bu hesap bir temsilci (agent) hesabı — yönetici girişinden erişilemez.',
            targetUrl,
          });
          setKcReady(true);
          Promise.resolve(activeKc.logout({ redirectUri: targetUrl })).catch(() => {});
          return;
        }
        if (!isAdminLogin && !ALL_OPS_ROLES.includes(kcUser.role)) {
          // ops-dashboard'a ait olmayan rol — diğer portallara yönlendir
          const domain = window.location.hostname.split('.').slice(-4).join('.');
          const target = kcUser.role === 'super_admin'
            ? `https://admin.${domain}/login`
            : `https://client.${domain}/tenant-login`;
          setKcMismatch({
            message: 'Bu hesap bu portala ait değil.',
            targetUrl: target,
          });
          setKcReady(true);
          Promise.resolve(activeKc.logout({ redirectUri: target })).catch(() => {});
          return;
        }

        // super_admin: ops-dashboard'a ait değil — admin portala yönlendir.
        if (kcUser.role === 'super_admin') {
          const agentSuffix = clientConfig.REACT_APP_AGENT_SUFFIX || '.app.dev.arvis.com.tr';
          const baseDomain = agentSuffix.replace(/^\.app\./, '');
          activeKc.logout({ redirectUri: `https://admin.${baseDomain}/login` });
          return;
        }

        // Token'ı yalnızca Zustand store'a kaydet — localStorage'a YAZMA (XSS güvenliği)
        setUser(kcUser);
        setToken(activeKc.token);
        // Temizlik: önceki legacy oturumun artıkları varsa sil
        localStorage.removeItem('token');
        localStorage.removeItem('user');

        // KC token sub = KC UUID ≠ internal DB UUID.
        // Fetch /api/auth/me to get the real users.id so agent-status and other
        // FK-constrained DB operations use the correct internal user ID.
        try {
          const meResp = await api.get('auth/me');
          if (meResp.data) {
            setUser({ ...kcUser, ...meResp.data });
          }
        } catch (_) {
          // Non-fatal: kcUser remains with KC sub as id; DB writes using id may fail
        }

        // onTokenExpired: store'u güncelle — token memory-only, hiçbir storage'a yazma.
        activeKc.onTokenExpired = () => {
          activeKc.updateToken(60).then(refreshed => {
            if (refreshed) setToken(activeKc.token);
          }).catch(() => activeKc.login());
        };

        setKcReady(true);

        // Subdomain kontrolü — bare app.domain'den geldiyse doğru tenant subdomain'e yönlendir
        const subdomain = decoded.subdomain;
        const hostname = window.location.hostname;
        const agentSuffix2 = clientConfig.REACT_APP_AGENT_SUFFIX || '.app.dev.arvis.com.tr';
        const expectedHost = subdomain ? `${subdomain}${agentSuffix2}` : null;
        if (expectedHost && hostname !== expectedHost) {
          const targetPath = OPS_ADMIN_PAGE_ROLES.includes(kcUser.role) ? '/admin' : '/agent';
          window.location.replace(`https://${expectedHost}${targetPath}`);
          return;
        }

        // Rol bazlı yönlendirme
        const currentPath = window.location.pathname;
        if (currentPath === '/' || currentPath === '/login' || currentPath === '/admin-login') {
          if (OPS_ADMIN_PAGE_ROLES.includes(kcUser.role)) {
            window.location.replace('/admin');
          } else if (kcUser.role === 'agent') {
            window.location.replace('/agent');
          } else if (kcUser.role === 'company_admin') {
            window.location.replace('/portal/dashboard');
          }
        }
      })
      .catch((err) => {
        logger.error('[KC] Init hatası:', err);
        setKcError(err?.message || 'Kimlik doğrulama başlatılamadı');
        setKcReady(true);
      });
  }, [setUser, setToken]);

  if (kcMismatch) {
    return (
      <div className="min-h-screen h-[100vh] flex items-center justify-center bg-surface-base">
        <div className="w-96 bg-surface-card shadow-xl rounded-2xl p-6">
          <div className="flex flex-col items-center text-center p-6">
            <h2 className="text-lg font-semibold text-error mb-2">Erişim reddedildi</h2>
            <p className="text-sm text-text-muted mb-4">{kcMismatch.message}</p>
            <button className="mt-2 px-4 py-2 bg-brand-500 text-white rounded-lg" onClick={() => { window.location.href = kcMismatch.targetUrl; }}>Doğru giriş sayfasına git</button>
          </div>
        </div>
      </div>
    );
  }
  if (!kcReady) {
    return (
      <div className="min-h-screen h-[100vh] flex items-center justify-center bg-surface-base">
        <div className="w-96 bg-surface-card shadow-xl rounded-2xl p-6">
          <div className="flex flex-col items-center text-center p-6">
            <div className="animate-spin h-10 w-10 border-4 border-accent border-t-transparent rounded-full"></div>
            <h2 className="text-lg font-semibold text-text-primary mt-4">Giriş Yapılıyor</h2>
          </div>
        </div>
      </div>
    );
  }
  if (kcError) {
    return (
      <div className="min-h-screen h-[100vh] flex items-center justify-center bg-surface-base">
        <div className="w-96 bg-surface-card shadow-xl rounded-2xl p-6">
          <div className="flex flex-col items-center text-center p-6">
            <h2 className="text-lg font-semibold text-error mb-2">Giriş başlatılamadı</h2>
            <p className="text-sm text-text-muted mb-4">{kcError}</p>
            <p className="text-xs text-text-disabled">Tarayıcı eklentisi (reklam engelleyici, antivirüs) script çalıştırmayı engelliyor olabilir. Gizli sekmede veya eklentiler kapalıyken tekrar deneyin.</p>
            <button className="mt-4 px-4 py-2 bg-brand-500 text-white rounded-lg" onClick={() => window.location.reload()}>Tekrar Dene</button>
          </div>
        </div>
      </div>
    );
  }
  return children;
};

function App() {
  const [useKc, setUseKc] = useState(null);

  useEffect(() => {
    axios.get('/api/public/auth-config').then(r => setUseKc(!!r.data?.useKeycloak)).catch(() => setUseKc(false));
  }, []);

  if (useKc === null) return (
    <div className="min-h-screen h-[100vh] flex items-center justify-center bg-surface-base">
      <div className="animate-spin h-10 w-10 border-4 border-accent border-t-transparent rounded-full"></div>
    </div>
  );

  const Wrapper = useKc ? KeycloakWrapper : AuthWrapper;

  return (
    <QueryClientProvider client={queryClient}>
    <ErrorBoundary>
    <Router>
      <Wrapper>
        <Suspense fallback={
          <div className="min-h-screen h-[100vh] flex items-center justify-center bg-surface-base p-4">
            <div className="w-96 bg-surface-card shadow-xl rounded-2xl p-6">
              <div className="flex flex-col items-center text-center p-6">
                <div className="animate-spin h-10 w-10 border-4 border-accent border-t-transparent rounded-full"></div>
                <h2 className="text-lg font-semibold text-text-primary mt-4">Yükleniyor</h2>
                <p className="text-sm text-text-muted">Sayfa hazırlanıyor...</p>
              </div>
            </div>
          </div>
        }>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            {/* /admin-login — ayrı KC client (ops-dashboard-admin) üzerinden admin/supervisor girişi */}
            <Route path="/admin-login" element={<LoginPage adminLogin />} />

            <Route path="/admin" element={
              <AdminRoute>
                <Navigate to="/portal/dashboard" replace />
              </AdminRoute>
            } />

            <Route path="/agent" element={
              <AgentRoute>
                <AgentLayout>
                  <AgentPage />
                </AgentLayout>
              </AgentRoute>
            } />

            <Route path="/agent/dashboard" element={
              <AgentRoute>
                <AgentLayout>
                  <AgentPersonalStats />
                </AgentLayout>
              </AgentRoute>
            } />

            <Route path="/portal/*" element={
              <CompanyAdminRoute>
                <PortalLayout />
              </CompanyAdminRoute>
            } />

            <Route path="/invite" element={<InvitePage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </Wrapper>

      <Toaster position="top-right" />
    </Router>
    </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
